import * as React from 'react';
import { compose, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { ReportType } from 'types';
import { coalesce } from 'util/coalesce';
import { TableColumnArray } from 'logic/reports/tableColumns';
import { getTextKeyForColumn } from 'logic/reports/getTextKeyForColumn';
import { Fragment } from 'react';

type OwnProps = {
    reportType: ReportType;
    columns: TableColumnArray;
};

const renderData = (reportType: ReportType, columns: TableColumnArray, isGrouped: boolean, styles: OemProps['styles'], t: TranslateProps['t']) =>
    [1, 2, 3].map(row =>
        <tr
            key={ `${row}` }
            style={ styles.TR }
        >
            { isGrouped && row === 1 &&
                <th
                    rowSpan={ 3 }
                    style={ styles.TH }
                />
            }
            { columns.map(c =>
                <td
                    key={ `${c.name}` }
                    style={ styles.TD }
                >
                    { t(getTextKeyForColumn(reportType, c.name)) }
                </td>
            ) }
        </tr>
    )
;

// Calculates the number of nodes in a tree with binary branches and three leaves on each bottom branch
const calculateRowSpan = (parentCount: number) =>
    parentCount > 0
        ? Math.pow(2, parentCount) - 2 + 3 * Math.pow(2, parentCount - 1)
        : 0
;


function renderGroup(
    reportType: ReportType,
    groups: TableColumnArray,
    groupDepth: number,
    isFirstBranch: boolean,
    columns: TableColumnArray,
    styles: OemProps['styles'],
    t: TranslateProps['t'],
): React.ReactNode {
    const group = groups[groupDepth];

    return (
        [1, 2].map(y =>
            <Fragment key={ `${group.name}-${y}` }>
                <tr
                    style={ styles.TR }
                >
                    { groupDepth > 0 && y === 1 &&
                        <th
                            rowSpan={ calculateRowSpan(groups.length - groupDepth + 1) }
                            style={ styles.TH }
                        />
                    }
                    <th
                        colSpan={ columns.length }
                        style={ styles.TH }
                    >
                        { t(getTextKeyForColumn(reportType, group.name)) }
                    </th>
                </tr>
                { renderGroupOrData(reportType, groups, groupDepth + 1, isFirstBranch && y === 1, columns.slice(1), true, styles, t) }
            </Fragment>
        )
    );
}

function renderGroupOrData(
    reportType: ReportType,
    groups: TableColumnArray,
    groupDepth: number,
    isFirstBranch: boolean,
    columns: TableColumnArray,
    isGrouped: boolean,
    styles: OemProps['styles'],
    t: TranslateProps['t'],
): React.ReactNode {
    return groupDepth < groups.length
        ? renderGroup(reportType, groups, groupDepth, isFirstBranch, columns, styles, t)
        : renderData(reportType, columns, isGrouped, styles, t);
}

const RawTablePreview = ({ reportType, columns, groups, styles, t }: OwnProps & RecomposeProps & OemProps & TranslateProps) =>
    <table
        className="table-preview"
        style={ styles.Table }
    >
        <thead
            style={ styles.THead }
        >
            <tr
                style={ styles.TR }
            >
                { columns.map((c, i) =>
                    <th
                        key={ `${i}-${c.name}` }
                        style={ styles.TH }
                    >
                        { typeof c.groupIndex === 'undefined' ? t(getTextKeyForColumn(reportType, c.name)) : '' }
                    </th>
                ) }
            </tr>
        </thead>
        <tbody>
            { renderGroupOrData(reportType, groups, 0, true, columns, false, styles, t) }
        </tbody>
    </table>
;

type RecomposeProps = {
    columns: TableColumnArray;
    groups: TableColumnArray;
};

const prepareColumns = (columns: TableColumnArray): TableColumnArray =>
    columns
        .filter(c => typeof c.visibleIndex === 'number') // Remove hidden columns
        .sort((a, b) => coalesce(a.visibleIndex, 99) - coalesce(b.visibleIndex, 99))
;

const prepareGroups = (columns: TableColumnArray): TableColumnArray =>
    columns
        .filter(c => typeof c.groupIndex === 'number')
        .sort((a, b) => a.groupIndex! - b.groupIndex!)
;

export const TablePreview = compose<OwnProps & OemProps & TranslateProps, OwnProps>(
    translate(),
    skin,
    withProps(({ columns, styles }: OemProps & OwnProps) => ({
        columns: prepareColumns(columns),
        groups: prepareGroups(columns),
        styles: {
            ...styles,
            Table: {
                ...styles.Table,
                fontSize: '.8em',
            },
            TH: {
                ...styles.Bordered,
                textAlign: 'left',
                padding: '0 1em',
                whiteSpace: 'nowrap',
            },
            TD: {
                textAlign: 'left',
                padding: '0 1em',
                opacity: .8,
            },
        },
    })),
)(RawTablePreview);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/tableLayout/TablePreview.tsx